import React from "react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, Inbox, RotateCw } from "lucide-react";

/* Shared design tokens + primitives for the admin dashboard.
   Kept in one place so every component (KPI cards, charts, tables,
   panels) stays visually consistent. */

export const COLORS = {
  indigo: "#4338ca",
  indigoSoft: "#6366f1",
  purple: "#9333ea",
  amber: "#d97706",
  emerald: "#059669",
  sky: "#0284c7",
  rose: "#e11d48",
  slate: "#64748b",
  grid: "#eef0f6",
};

export const SPINE = {
  indigo: "bg-indigo-600", sky: "bg-sky-600", emerald: "bg-emerald-600",
  amber: "bg-amber-600", purple: "bg-purple-600", rose: "bg-rose-600", slate: "bg-slate-400",
};
export const EYEBROW = {
  indigo: "text-indigo-600", sky: "text-sky-600", emerald: "text-emerald-600",
  amber: "text-amber-600", purple: "text-purple-600", rose: "text-rose-600", slate: "text-slate-500",
};
export const CHIP = {
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  sky: "bg-sky-50 text-sky-700 ring-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  purple: "bg-purple-50 text-purple-700 ring-purple-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

export const SEVERITY = {
  critical: { label: "קריטית", chip: "bg-rose-100 text-rose-800 ring-rose-300" },
  high: { label: "גבוהה", chip: "bg-rose-50 text-rose-700 ring-rose-200" },
  medium: { label: "בינונית", chip: "bg-amber-50 text-amber-700 ring-amber-200" },
  low: { label: "נמוכה", chip: "bg-slate-100 text-slate-600 ring-slate-200" },
};

export function formatILS(n) {
  return new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(n ?? 0);
}
export function formatPct(n) {
  return `${n > 0 ? "+" : ""}${n}%`;
}

/** Trend arrow + colored percentage. `invert` = true for metrics where a
 * decrease is the good outcome (e.g. cancellations, failed payments). */
export const TrendIndicator = ({ deltaPct, invert = false, className = "" }) => {
  if (deltaPct === undefined || deltaPct === null) return null;
  const isUp = deltaPct >= 0;
  const good = invert ? !isUp : isUp;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${good ? "text-emerald-600" : "text-rose-600"} ${className}`}>
      {isUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {formatPct(deltaPct)}
    </span>
  );
};

/** Index-card panel: colored spine + eyebrow label, used for every
 * chart/table/list section on the page. Handles loading/error/empty. */
export const Panel = ({
  eyebrow, title, subtitle, accent = "indigo", action,
  loading = false, error = null, onRetry, empty = false, emptyText = "אין נתונים להצגה בטווח שנבחר",
  className = "", bodyClassName = "", children,
}) => (
  <div className={`relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}>
    <span className={`absolute inset-y-0 right-0 w-1.5 ${SPINE[accent]}`} />
    <div className="p-4 sm:p-5 pr-5 sm:pr-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-widest ${EYEBROW[accent]}`}>{eyebrow}</p>
          <h3 className="text-sm sm:text-base font-bold text-gray-900 mt-0.5 truncate">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>

      <div className={bodyClassName}>
        {loading ? (
          <SkeletonBlock />
        ) : error ? (
          <ErrorState onRetry={onRetry} />
        ) : empty ? (
          <EmptyState text={emptyText} />
        ) : (
          children
        )}
      </div>
    </div>
  </div>
);

export const SkeletonBlock = ({ rows = 4 }) => (
  <div className="animate-pulse space-y-2.5 py-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-3.5 rounded bg-gray-100" style={{ width: `${85 - i * 12}%` }} />
    ))}
  </div>
);

export const EmptyState = ({ text = "אין נתונים להצגה" }) => (
  <div className="flex flex-col items-center justify-center py-8 text-gray-400 gap-2">
    <Inbox className="w-6 h-6" />
    <p className="text-xs font-medium">{text}</p>
  </div>
);

export const ErrorState = ({ text = "אירעה שגיאה בטעינת הנתונים", onRetry }) => (
  <div className="flex flex-col items-center justify-center py-8 text-rose-500 gap-2">
    <AlertTriangle className="w-6 h-6" />
    <p className="text-xs font-medium">{text}</p>
    {onRetry && (
      <button onClick={onRetry} className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 hover:text-rose-700 mt-1">
        <RotateCw className="w-3 h-3" /> נסה שוב
      </button>
    )}
  </div>
);

/** KPI card with icon, value, trend, and a tiny sparkline. */
export const KpiCard = ({ icon: Icon, label, value, deltaPct, invert = false, spark, accent = "indigo", loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <SkeletonBlock rows={3} />
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-gray-400">
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-[10px] font-bold uppercase tracking-wide truncate">{label}</span>
          </div>
          <p className="text-xl font-extrabold text-gray-900 mt-1">{value}</p>
          <TrendIndicator deltaPct={deltaPct} invert={invert} className="mt-1" />
        </div>
        {spark && (
          <div className="w-16 h-10 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={spark.map((v) => ({ v }))}>
                <defs>
                  <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS[accent]} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={COLORS[accent]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={COLORS[accent]} strokeWidth={2} fill={`url(#spark-${label})`} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export const ChartTooltipStyle = { borderRadius: 12, border: "1px solid #eef0f6", fontSize: 12 };